import { Matrix } from 'pixi.js';
import type { TownPavingArtFamily } from '@/modules/game-domain/authored-town.types';
import { TILE_WIDTH, TILE_HEIGHT } from '@/shared/constants';
import { worldToScreen } from './coordinates';

const MATERIALS = {
  ruins_reclaimed_stone: { textureKey: 'terrain.paving.ruins_reclaimed_stone.v1', color: 0x73706a, sourceSpanWU: 4 },
  volcanic_basalt: { textureKey: 'terrain.paving.volcanic_basalt.v1', color: 0x50474a, sourceSpanWU: 4 },
  astral_opal: { textureKey: 'terrain.paving.astral_opal.v1', color: 0x8495a7, sourceSpanWU: 4 },
  meadow_limestone: { textureKey: 'terrain.paving.meadow_limestone.v1', color: 0x9c9787, sourceSpanWU: 8 },
  forest_moss_stone: { textureKey: 'terrain.paving.forest_moss_stone.v1', color: 0x555647, sourceSpanWU: 4 },
  canyon_sandstone: { textureKey: 'terrain.paving.canyon_sandstone.v1', color: 0xb18a56, sourceSpanWU: 4 },
  swamp_wet_stone: { textureKey: 'terrain.paving.swamp_wet_stone.v1', color: 0x394750, sourceSpanWU: 4 },
} satisfies Record<TownPavingArtFamily, { textureKey: string; color: number; sourceSpanWU: number }>;

/** Explicit saved metadata opts in; a biome name never changes legacy paving. */
export function authoredPavingMaterial(family: string | undefined): { textureKey: string; color: number; sourceSpanWU: number } | undefined {
  return family && Object.prototype.hasOwnProperty.call(MATERIALS, family) ? MATERIALS[family as TownPavingArtFamily] : undefined;
}

export const PAVING_SOURCE_SPAN_WU = 4;

/** Project a top-down material's two axes onto the exact 96x48 world grid.
 * Mirrored-repeat spans two source tiles, so phase anchors move by whole periods
 * periods. This keeps all streets, squares and thresholds aligned while UVs
 * stay bounded near remote towns on mediump GPUs.
 */
export function authoredPavingMatrix(points: readonly { readonly x: number; readonly y: number }[], width: number, height: number, sourceSpanWU = PAVING_SOURCE_SPAN_WU): Matrix {
  const period = sourceSpanWU * 2;
  const origin = worldToScreen(Math.floor(Math.min(...points.map(p => p.x)) / period) * period,
    Math.floor(Math.min(...points.map(p => p.y)) / period) * period);
  const xScale = sourceSpanWU / width, yScale = sourceSpanWU / height;
  return new Matrix((TILE_WIDTH / 2) * xScale, (TILE_HEIGHT / 2) * xScale,
    -(TILE_WIDTH / 2) * yScale, (TILE_HEIGHT / 2) * yScale, origin.x, origin.y);
}
